import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, Download, ExternalLink, Clock, Sparkles, Flame, Eye } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ProductModal, type Product } from '@/components/ProductModal'
import { getProductsByCategory } from '@/data/products'

const ease = [0.16, 1, 0.3, 1] as [number, number, number, number]

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease },
  }),
}

const processSteps = [
  { key: 'step1', icon: Flame },
  { key: 'step2', icon: Clock },
  { key: 'step3', icon: Sparkles },
]

const validCategories = ['all', 'panettoni', 'colombe', 'pasticceria']

export function ProductsPage() {
  const { t } = useTranslation('common')
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryParam = searchParams.get('category')
  const initialCategory = categoryParam && validCategories.includes(categoryParam) ? categoryParam : 'all'
  const [activeCategory, setActiveCategory] = useState(initialCategory)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleCategoryChange = (key: string) => {
    setActiveCategory(key)
    if (key === 'all') {
      searchParams.delete('category')
    } else {
      searchParams.set('category', key)
    }
    setSearchParams(searchParams, { replace: true })
  }

  const categories = [
    { key: 'all', label: t('products.categories.all') },
    { key: 'panettoni', label: t('products.categories.panettoni') },
    { key: 'colombe', label: t('products.categories.colombe') },
    { key: 'pasticceria', label: t('products.categories.pasticceria') },
  ]

  const filteredProducts = getProductsByCategory(activeCategory)

  const catalogs = [
    { key: 'christmas', filename: 'catalogo-natale.pdf' },
    { key: 'easter', filename: 'catalogo-pasqua.pdf' },
  ]

  const openProductModal = (product: Product) => {
    setSelectedProduct(product)
    setIsModalOpen(true)
  }

  const closeProductModal = () => {
    setIsModalOpen(false)
    setTimeout(() => setSelectedProduct(null), 300)
  }

  return (
    <>
      {/* 1. Intro / Brand Promise */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-0.5 bg-brand mx-auto mb-8" />
          <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-4">
            <span className="w-8 h-px bg-brand" />
            {t('products.intro.label')}
            <span className="w-8 h-px bg-brand" />
          </span>
          <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-neutral-900 leading-snug">
            {t('products.intro.tagline')}
          </p>
          <div className="w-16 h-0.5 bg-brand mx-auto mt-8" />
        </div>
      </section>

      {/* 2. Category Tabs + Product Grid */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease }}
            className="flex flex-wrap justify-center gap-2 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => handleCategoryChange(category.key)}
                className={cn(
                  'px-6 py-2 font-heading font-medium rounded-full transition-all duration-200',
                  activeCategory === category.key
                    ? 'bg-brand text-neutral-900 hover:bg-brand-dark hover:text-neutral-900 shadow-md'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                )}
              >
                {category.label}
              </button>
            ))}
          </motion.div>

          {/* Products Grid with AnimatePresence */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {filteredProducts.map((product, i) => (
                <motion.div
                  key={product.id}
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  onClick={() => openProductModal(product)}
                  className={cn(
                    'group bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden',
                    'hover:shadow-xl hover:border-brand/30',
                    'transition-all duration-300 cursor-pointer',
                    'transform hover:-translate-y-1'
                  )}
                >
                  {/* Image Container */}
                  <div className="relative aspect-square bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center overflow-hidden">
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1 z-10">
                      {product.isNew && (
                        <span className="px-2 py-0.5 bg-brand text-neutral-900 text-[10px] font-bold uppercase tracking-wider rounded-full">
                          {t('productLabels.new')}
                        </span>
                      )}
                      {product.isClassic && (
                        <span className="px-2 py-0.5 bg-amber-600 text-white text-[10px] font-bold uppercase tracking-wider rounded-full">
                          {t('productLabels.classic')}
                        </span>
                      )}
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0">
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-white text-neutral-900 rounded-full shadow-lg font-medium text-sm">
                          <Eye className="w-4 h-4" />
                          {t('productLabels.discover')}
                        </span>
                      </div>
                    </div>

                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <p className="text-xs text-brand font-medium uppercase tracking-wider mb-1">
                      {product.subcategory || product.category}
                    </p>
                    <h3 className="font-heading font-semibold text-neutral-900 group-hover:text-brand transition-colors">
                      {product.name}
                    </h3>
                    {product.weight && (
                      <p className="text-sm text-neutral-500 mt-1">
                        {product.weight}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Products Count */}
          <p className="text-center text-sm text-neutral-500 mt-8">
            {t('productLabels.productCount', { count: filteredProducts.length })} {activeCategory !== 'all' && t('productLabels.inCategory', { category: categories.find(c => c.key === activeCategory)?.label.toLowerCase() })}
          </p>
        </div>
      </section>

      {/* 3. Process / Storytelling */}
      <section className="bg-neutral-900 text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, ease }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              {t('products.process.title')}
            </h2>
            <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
              {t('products.process.subtitle')}
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0 relative">
            {/* Gold connecting line — desktop only */}
            <div className="hidden md:block absolute top-12 left-[16.67%] right-[16.67%] h-px bg-brand/40" />

            {processSteps.map((step, i) => (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: i * 0.2, duration: 0.7, ease }}
                className="relative flex flex-col items-center text-center px-6"
              >
                {/* Step number + icon */}
                <div className="relative z-10 w-24 h-24 rounded-full border-2 border-brand/30 bg-neutral-800 flex items-center justify-center mb-6">
                  <step.icon className="w-8 h-8 text-brand" />
                  <span className="absolute -top-3 -right-1 w-8 h-8 bg-brand text-neutral-900 rounded-full flex items-center justify-center font-heading font-bold text-sm">
                    {i + 1}
                  </span>
                </div>

                {/* Mobile gold connector — between steps */}
                {i < processSteps.length - 1 && (
                  <div className="md:hidden w-px h-8 bg-brand/40 absolute -bottom-8 left-1/2 -translate-x-1/2" />
                )}

                <h3 className="text-xl font-heading font-semibold text-white mb-3">
                  {t(`products.process.${step.key}.title`)}
                </h3>
                <p className="text-neutral-400 leading-relaxed max-w-xs">
                  {t(`products.process.${step.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Catalogs Section */}
      <section className="section-padding bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, ease }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-4">
              <span className="w-8 h-px bg-brand" />
              {t('products.catalogs.title')}
              <span className="w-8 h-px bg-brand" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-4">
              {t('products.catalogs.title')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              {t('products.catalogs.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {catalogs.map((catalog, i) => (
              <motion.a
                key={catalog.key}
                href={`/documents/${catalog.filename}`}
                target="_blank"
                rel="noopener noreferrer"
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                whileHover={{ y: -4 }}
                className="relative flex items-center gap-4 p-6 bg-white rounded-xl shadow-sm border-l-4 border-brand hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center shrink-0">
                  <Download className="w-5 h-5 text-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-heading font-semibold text-neutral-900 block">
                    {t(`products.catalogs.${catalog.key}`)}
                  </span>
                  <span className="text-sm text-neutral-500">{t('buttons.download')}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-400 shrink-0" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* 5. E-Shop CTA */}
      <section className="relative bg-neutral-900 text-white py-16 md:py-20 overflow-hidden">
        {/* Decorative dots pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease }}
          >
            <span className="inline-flex items-center gap-3 text-brand font-heading font-medium tracking-wider uppercase text-sm mb-6">
              <span className="w-8 h-px bg-brand" />
              {t('products.shop.label')}
              <span className="w-8 h-px bg-brand" />
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              {t('products.shop.title')}
            </h2>
            <p className="text-lg text-neutral-400 mb-10 max-w-2xl mx-auto">
              {t('products.shop.description')}
            </p>
            <motion.a
              href={import.meta.env.VITE_ESHOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.02 }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-brand text-neutral-900 font-heading font-semibold text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <span>{t('products.shop.cta')}</span>
              <ExternalLink className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={closeProductModal}
      />
    </>
  )
}
